import {
  ArrowLeft,
  ArrowLeftRight,
  Pencil,
  PackageX,
  QrCode,
  ScanLine,
  Wrench,
  Undo2,
} from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ASSET_CONDITION_LABELS,
  ASSET_OWNERSHIP_LABELS,
  ASSET_STATUS_LABELS,
  canAssignAsset,
  canOpenRepair,
  canReturnAsset,
  daysBetween,
  formatPlainDate,
  REPAIR_STATUS_LABELS,
  STOCK_TAKE_WINDOW_DAYS,
  tidyText,
  today,
} from '@cfl/assets-shared';

import { toast } from 'sonner';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
} from '@/components/cargofl-ui';
import { AssignDialog } from '@/routes/assign';
import { ReturnDialog } from '@/routes/return';
import { OpenRepairDialog } from '@/routes/repairs';
import { FullPageSpinner } from '@/components/shell/full-page-spinner';
import { Documents } from '@/components/documents';
import { PageContainer } from '@/components/shell/page-container';
import {
  assetStatusVariant,
  conditionVariant,
  ownershipVariant,
  PILL_CLASS,
  repairStatusVariant,
  warrantyVariant,
  WARRANTY_LABELS,
} from '@/lib/badges';
import { ApiError } from '@/lib/api';
import { SuggestionPicker } from '@/components/pickers';
import {
  useAsset,
  useLocationSuggestions,
  useVerifyAssets,
  type AssetDetail,
} from '@/lib/assets-api';
import { useAcknowledge } from '@/lib/operations-api';
import { useCan } from '@/lib/session';
import {
  useClearDeviceCredential,
  useRevealDeviceCredential,
} from '@/lib/operations-api';
import { useLightbox } from '@/components/lightbox';
import { documentUrl } from '@/lib/documents-api';
import { AssetFormDialog } from '@/routes/asset-form';
import { RetireAssetDialog } from '@/routes/asset-retire';

export function AssetDetailPage() {
  const { assetId } = useParams<{ assetId: string }>();
  const { data: asset, isPending, isError } = useAsset(assetId ?? null);
  const canManage = useCan('manageAssets');
  const canAssign = useCan('assignAssets');
  const canRepair = useCan('manageRepairs');
  const [editing, setEditing] = React.useState(false);
  const [retiring, setRetiring] = React.useState(false);

  if (isPending) return <FullPageSpinner />;
  if (isError || !asset) {
    return (
      <PageContainer>
        <Card>
          <CardContent className="py-12 text-center sm:py-12">
            <p className="text-sm text-muted-foreground">That asset no longer exists.</p>
            <Button asChild variant="secondary" size="sm" className="mt-4">
              <Link to="/assets">Back to the register</Link>
            </Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 h-7 text-muted-foreground"
          >
            <Link to="/assets">
              <ArrowLeft className="mr-1 size-3.5" /> Register
            </Link>
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="at-num text-xl font-bold tracking-[-0.02em]">{asset.code}</h2>
            <Badge variant={assetStatusVariant(asset.status)} className={PILL_CLASS}>
              {ASSET_STATUS_LABELS[asset.status]}
            </Badge>
            <Badge variant={warrantyVariant(asset.warranty)} className={PILL_CLASS}>
              {WARRANTY_LABELS[asset.warranty]}
            </Badge>
            {/* Company-owned is the default and says nothing worth a pill. Anything
                else changes what happens at the holder’s exit, so it is shown up here
                with the other two facts that decide what can be done with the asset. */}
            {asset.ownership === 'EMPLOYEE' && (
              <Badge variant={ownershipVariant(asset.ownership)} className={PILL_CLASS}>
                {ASSET_OWNERSHIP_LABELS.EMPLOYEE}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {asset.name}
            {asset.brand || asset.model
              ? ` · ${[asset.brand, asset.model].filter(Boolean).join(' ')}`
              : ''}
            {` · ${asset.categoryName}`}
          </p>
        </div>

        {canManage && (
          <div className="flex gap-2">
            {/* One sticker, printed from the same sheet the register prints in bulk. */}
            <Button hint={"Print this asset's label"} asChild size="sm" variant="secondary">
              <Link to={`/assets/labels?ids=${asset.id}`}>
                <QrCode className="mr-1.5 size-4" /> Label
              </Link>
            </Button>
            <Button hint={"Change this asset's details"} size="sm" variant="secondary" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 size-4" /> Edit
            </Button>
            {/* Hidden once the asset is already disposed of - there is nothing left
                to take out of service. */}
            {asset.status !== 'DISPOSED' && (
              <Button hint={"Retire, dispose of or write off this asset"} size="sm" variant="secondary" onClick={() => setRetiring(true)}>
                <PackageX className="mr-1.5 size-4" /> Take out of service
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Details</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <dl className="divide-y text-sm">
              <Row label="Ownership" value={ASSET_OWNERSHIP_LABELS[asset.ownership]} />
              <Row label="Location" value={asset.location} />
              <Row label="Serial" value={asset.serial} mono />
              <Row
                label="Purchased"
                value={asset.purchasedAt && formatPlainDate(asset.purchasedAt)}
              />
              <Row label="Price" value={asset.price} mono />
              <Row label="Vendor" value={asset.vendor} />
              <Row label="Invoice" value={asset.invoiceNo} mono />
              <Row
                label="Warranty"
                value={
                  asset.warrantyStart || asset.warrantyEnd
                    ? `${asset.warrantyStart ? formatPlainDate(asset.warrantyStart) : '?'} → ${
                        asset.warrantyEnd ? formatPlainDate(asset.warrantyEnd) : '?'
                      }`
                    : null
                }
              />
              {/* Only rendered when recorded. A register that is mostly furniture would
                  otherwise carry three permanently empty rows on every screen. */}
              {asset.ramGb !== null && <Row label="RAM" value={`${asset.ramGb} GB`} mono />}
              {asset.cpu !== null && <Row label="Processor" value={asset.cpu} />}
              {asset.storageGb !== null && (
                <Row label="Storage" value={`${asset.storageGb} GB`} mono />
              )}
              <Row label="Registered by" value={asset.createdByName} />
            </dl>

            <KnownCondition asset={asset} />
            {asset.notes && (
              <p className="mt-3 whitespace-pre-wrap rounded-md bg-muted/60 p-3 text-xs leading-relaxed">
                {tidyText(asset.notes)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Currently</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {asset.holder ? (
              <div className="space-y-1">
                <p className="text-sm">
                  Issued to{' '}
                  <Link
                    to={`/employees/${asset.holder.id}`}
                    className="font-medium hover:underline"
                  >
                    {asset.holder.name}
                  </Link>{' '}
                  <span className="at-num text-muted-foreground">
                    ({asset.holder.employeeCode})
                  </span>
                </p>
                <ConfirmOnBehalf asset={asset} canManage={canManage} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Not issued to anyone.
                {asset.status === 'AVAILABLE' ? ' It is free to assign.' : ''}
              </p>
            )}

            {/* The card said "free to assign" and "issued to Ravi" for months without
                offering either action, which left the one screen that knows the most
                about an asset as the one place you could do the least with it. */}
            <Handover asset={asset} canAssign={canAssign} canRepair={canRepair} />

            {/* Who has it and whether anyone has seen it are different questions, and
                this card is where both belong: an asset issued to someone who left
                eighteen months ago is "held by" them and has been seen by nobody. */}
            <PhysicalCheck asset={asset} canManage={canManage} />
          </CardContent>
        </Card>

        {/* Full width, on its own row. Filenames are long and a two-column card
            truncates every one of them to something unreadable. */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Documents</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Documents
              entity="asset"
              entityId={asset.id}
              canManage={canManage}
              noun="invoice"
              emptyHint="No invoice or paperwork attached yet. The invoice is where the purchase date, price and warranty come from — attach it and those stop being guesses."
            />
          </CardContent>
        </Card>
      </div>

      <LifecycleTimeline asset={asset} />

      <AssetFormDialog open={editing} onOpenChange={setEditing} assetId={asset.id} />
      <RetireAssetDialog asset={asset} open={retiring} onOpenChange={setRetiring} />
    </PageContainer>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2">
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-right ${mono ? 'at-num' : ''} ${value ? '' : 'text-muted-foreground'}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

/**
 * When somebody last physically laid eyes on this, and the button that says so.
 *
 * The register's oldest blind spot: it always knew who SHOULD have a laptop and never
 * whether anyone had seen it. Retired assets are left out entirely - there is nothing
 * to go and look for.
 *
 * The dialog asks for the location as well as the confirmation, because those are one
 * act. Somebody standing in front of the asset is the only person who knows where it
 * is, and making them save the location in a separate Edit afterwards is how the
 * location column stays empty forever.
 */
function PhysicalCheck({ asset, canManage }: { asset: AssetDetail; canManage: boolean }) {
  const verify = useVerifyAssets();
  const { data: suggestions } = useLocationSuggestions();
  const [dialog, setDialog] = React.useState(false);
  const [location, setLocation] = React.useState('');

  if (asset.status === 'RETIRED' || asset.status === 'DISPOSED') return null;

  const days = asset.lastVerifiedAt ? daysBetween(asset.lastVerifiedAt, today()) : null;
  const overdue = days === null || days > STOCK_TAKE_WINDOW_DAYS;

  const open = () => {
    setLocation(asset.location ?? '');
    setDialog(true);
  };

  const submit = async () => {
    try {
      await verify.mutateAsync({ ids: [asset.id], location: location.trim() || undefined });
      toast.success(`${asset.code} confirmed as seen today.`);
      setDialog(false);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t pt-3 text-xs">
      {asset.lastVerifiedAt ? (
        <span className={overdue ? 'text-warning' : 'text-muted-foreground'}>
          Last seen {formatPlainDate(asset.lastVerifiedAt)}
          {asset.lastVerifiedByName && ` by ${asset.lastVerifiedByName}`}
          {/* The age, not just the date. "12 Feb" means nothing without doing the
              subtraction, which is the step that gets skipped. */}
          {days !== null && ` · ${days} day${days === 1 ? '' : 's'} ago`}
        </span>
      ) : (
        <span className="text-muted-foreground">Nobody has physically checked this yet.</span>
      )}

      {canManage && (
        <Button hint={"Record that you physically saw this asset today"} size="sm" variant="secondary" className="h-7" onClick={open}>
          <ScanLine className="mr-1.5 size-3.5" /> I have seen it
        </Button>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm you have seen {asset.code}</DialogTitle>
            <DialogDescription>
              Records today against this asset as the last time anyone physically checked it.
              Unlike a handover confirmation this is re-stamped every time — the useful answer is
              always the most recent one, not the first.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label>Where is it?</Label>
            <SuggestionPicker
              noun="location"
              suggestions={suggestions?.locations}
              emptyText="No locations recorded yet — type the first"
              value={location}
              onChange={setLocation}
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              You are the person standing in front of it, so this is the moment the location is
              worth recording. Leave it as it is if nothing has moved.
            </p>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialog(false)}>
              Cancel
            </Button>
            <Button hint={"Record that you saw it today"} onClick={() => void submit()} disabled={verify.isPending}>
              {verify.isPending ? 'Recording…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * IT recording a confirmation the holder gave them elsewhere - on the phone, on a
 * signed handover sheet.
 *
 * Shown only while the open assignment is unconfirmed, and only to someone who manages
 * hardware. It writes the administrator's id against the row rather than the holder's,
 * so the timeline can say who actually said it. The note is required for that reason:
 * it is the whole evidence of what the employee told them.
 */
/**
 * Assign or return, from the screen that knows an asset best.
 *
 * Both conditions are the shared predicates the API judges the request by, so neither
 * button is ever offered on an asset the API is going to refuse - under repair earns
 * neither, because the thing is at the vendor.
 *
 * The return target's accessories come off the OPEN assignment rather than off `asset`.
 * The detail endpoint does not send a top-level `accessories` (only the list endpoint
 * does), so handing the record straight through would render an empty "what came back"
 * checklist and quietly record every charger as returned.
 */
function Handover({
  asset,
  canAssign,
  canRepair,
}: {
  asset: AssetDetail;
  canAssign: boolean;
  canRepair: boolean;
}) {
  const [issuing, setIssuing] = React.useState(false);
  const [returning, setReturning] = React.useState(false);
  const [repairing, setRepairing] = React.useState(false);
  const open = asset.assignments.find((a) => a.returnedAt === null);

  const canIssue = canAssign && canAssignAsset(asset.status, asset.holder !== null);
  const canTakeBack = canAssign && canReturnAsset(open !== undefined, asset.status);
  // Its own permission: IT owns hardware repairs, and manageRepairs is the predicate
  // the API guards the repair routes with.
  const canSendForRepair = canRepair && canOpenRepair(asset.status);
  if (!canIssue && !canTakeBack && !canSendForRepair) return null;

  return (
    <>
      <div className="mt-3 flex flex-wrap gap-2">
        {canIssue && (
          <Button hint={"Hand this asset to an employee"} size="sm" onClick={() => setIssuing(true)}>
            <ArrowLeftRight className="mr-1.5 size-4" /> Assign
          </Button>
        )}
        {canTakeBack && (
          <Button hint={"Record that the asset came back"} size="sm" variant="secondary" onClick={() => setReturning(true)}>
            <Undo2 className="mr-1.5 size-4" /> Return
          </Button>
        )}
        {/* The timeline below already lists every repair this asset has had and offered
            no way to add one - the history was readable and unwritable. */}
        {canSendForRepair && (
          <Button hint={"Record a repair or service for this asset"} size="sm" variant="secondary" onClick={() => setRepairing(true)}>
            <Wrench className="mr-1.5 size-4" /> Log repair
          </Button>
        )}
      </div>

      <OpenRepairDialog asset={asset} open={repairing} onOpenChange={setRepairing} />

      {issuing && <AssignDialog asset={asset} onOpenChange={setIssuing} />}
      {returning && open && (
        <ReturnDialog
          asset={{
            id: asset.id,
            code: asset.code,
            name: asset.name,
            holder: asset.holder,
            // Both read by requiresDeviceCredential, which decides whether the return
            // form asks for the machine's login.
            categoryId: asset.categoryId,
            ownership: asset.ownership,
            accessories: open.accessories,
            assignedAt: open.assignedAt,
          }}
          onOpenChange={setReturning}
        />
      )}
    </>
  );
}

function ConfirmOnBehalf({ asset, canManage }: { asset: AssetDetail; canManage: boolean }) {
  const open = asset.assignments.find((a) => a.returnedAt === null);
  const acknowledge = useAcknowledge();
  const [dialog, setDialog] = React.useState(false);
  const [note, setNote] = React.useState('');

  if (!open) return null;

  if (open.acknowledgedAt) {
    return (
      <p className="text-xs text-success">
        Confirmed {formatPlainDate(open.acknowledgedAt.slice(0, 10))}
        {open.acknowledgedByName && (
          <span className="text-muted-foreground">
            {' '}
            · recorded by {open.acknowledgedByName} on their behalf
          </span>
        )}
      </p>
    );
  }

  const submit = async () => {
    try {
      await acknowledge.mutateAsync({ id: open.id, note: note.trim() });
      toast.success(`${asset.code} confirmed on ${open.employee.name}'s behalf.`);
      setDialog(false);
      setNote('');
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : 'That could not be recorded.');
    }
  };

  return (
    <>
      <p className="flex flex-wrap items-center gap-2 text-xs text-warning">
        Not confirmed by them yet.
        {canManage && (
          <Button hint={"Record that the holder confirmed receipt, when they can't do it themselves"} size="sm" variant="secondary" className="h-7" onClick={() => setDialog(true)}>
            Confirm on their behalf
          </Button>
        )}
      </p>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm on {open.employee.name}&rsquo;s behalf</DialogTitle>
            <DialogDescription>
              This is recorded as <strong>you</strong> confirming it for them, not as them
              confirming it. Their own confirmation is stronger evidence, so use this only when they
              have told you and cannot record it themselves.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label>How did they confirm it?</Label>
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Confirmed over the phone. Signed the handover sheet on 26 Aug."
            />
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Required. Whoever reads this record later has only this line to weigh, and &ldquo;an
              administrator ticked it&rdquo; on its own is worth very little.
            </p>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialog(false)}>
              Cancel
            </Button>
            <Button hint={"Record that the holder confirmed receipt"} onClick={() => void submit()} disabled={acknowledge.isPending || !note.trim()}>
              {acknowledge.isPending ? 'Recording…' : 'Record confirmation'}
            </Button>
            {!note.trim() && <span className="self-center text-xs text-muted-foreground">Add a note first</span>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface TimelineEvent {
  date: string;
  title: React.ReactNode;
  detail?: React.ReactNode;
}

/**
 * Everything that ever happened to this asset, newest first.
 *
 * Assignments and repairs are interleaved into ONE list rather than shown as two
 * tables, because the question this answers is "what happened to this laptop", and
 * the answer is a sequence - it went to Ravi, came back scratched, went to the
 * vendor, came back, went to Priya. Two tables make the reader do that merge in their
 * head.
 */
function LifecycleTimeline({ asset }: { asset: AssetDetail }) {
  // Read here rather than threaded down from the page: revealing a stored password is
  // the only thing in this timeline that is permissioned, and passing a prop through
  // for it would put the check further from the button it guards.
  const canManage = useCan('manageAssets');
  const events: TimelineEvent[] = [];

  for (const a of asset.assignments) {
    events.push({
      date: a.assignedAt,
      title: (
        <>
          Issued to{' '}
          <Link to={`/employees/${a.employee.id}`} className="font-medium hover:underline">
            {a.employee.name}
          </Link>
        </>
      ),
      detail: (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge variant={conditionVariant(a.condition)} className={PILL_CLASS}>
            {ASSET_CONDITION_LABELS[a.condition]}
          </Badge>
          {a.acknowledgedAt ? (
            <span className="text-success">
              confirmed {formatPlainDate(a.acknowledgedAt.slice(0, 10))}
              {/* Named, not hidden: a confirmation somebody else recorded is weaker
                  evidence than the holder's own, and a reader has to be able to tell
                  the two apart. */}
              {a.acknowledgedByName && (
                <span className="text-muted-foreground">
                  {' '}
                  · recorded by {a.acknowledgedByName} on their behalf
                </span>
              )}
            </span>
          ) : (
            <span className="text-warning">never confirmed by the holder</span>
          )}
          {a.assignedByName && <span>· by {a.assignedByName}</span>}
          {a.accessories.length > 0 && (
            <span className="w-full text-muted-foreground">with: {a.accessories.join(', ')}</span>
          )}
          {/* Frozen at this handover. The whole point: a fault reported later can be
              read against what was already wrong on the day, rather than against the
              asset's list as it stands today. */}
          {a.conditionCheckedAtHandover !== null && a.faultsAtHandover.length > 0 && (
            <span className="w-full text-warning">
              already faulty: {a.faultsAtHandover.join(', ')}
            </span>
          )}
          {a.conditionNotesAtHandover && (
            <span className="w-full text-muted-foreground">{a.conditionNotesAtHandover}</span>
          )}
          {a.remarks && <span className="w-full text-muted-foreground">{a.remarks}</span>}
          <PhotoRow photos={a.handoverPhotos} label="At handover" />
        </span>
      ),
    });

    if (a.returnedAt && a.returnCondition) {
      events.push({
        date: a.returnedAt,
        title: (
          <>
            Returned by <span className="font-medium">{a.employee.name}</span>
          </>
        ),
        detail: (
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge variant={conditionVariant(a.returnCondition)} className={PILL_CLASS}>
              {ASSET_CONDITION_LABELS[a.returnCondition]}
            </Badge>
            {a.returnReason && <span>· {a.returnReason}</span>}
            {a.returnedByName && <span>· taken by {a.returnedByName}</span>}
            {a.returnRemarks && (
              <span className="w-full text-muted-foreground">{a.returnRemarks}</span>
            )}
            <PhotoRow photos={a.returnPhotos} label="On return" />
            <DeviceCredential assignment={a} canManage={canManage} />
          </span>
        ),
      });
    }
  }

  for (const r of asset.repairs) {
    events.push({
      date: r.startedAt,
      title: <>Sent for repair — {r.reason}</>,
      detail: (
        <span className="flex flex-wrap items-center gap-1.5">
          <Badge variant={repairStatusVariant(r.status)} className={PILL_CLASS}>
            {REPAIR_STATUS_LABELS[r.status]}
          </Badge>
          {r.vendor && <span>· {r.vendor}</span>}
          {r.cost && <span className="at-num">· {r.cost}</span>}
          {r.parts && <span>· parts: {r.parts}</span>}
          {r.completedAt && <span>· closed {formatPlainDate(r.completedAt)}</span>}
        </span>
      ),
    });
  }

  if (asset.purchasedAt) {
    events.push({
      date: asset.purchasedAt,
      title: <>Purchased{asset.vendor ? ` from ${asset.vendor}` : ''}</>,
      detail: asset.price ? <span className="at-num">{asset.price}</span> : undefined,
    });
  }

  // Lexicographic on YYYY-MM-DD is chronological, which is exactly why dates are
  // stored in that form - see PlainDate in @cfl/assets-shared.
  events.sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Lifecycle</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {events.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nothing has happened to this asset yet — it has never been issued or repaired.
          </p>
        ) : (
          <ol className="relative space-y-4 border-l pl-5">
            {events.map((event, index) => (
              <li key={`${event.date}-${index}`} className="relative">
                <span className="absolute -left-[1.4rem] top-1.5 size-2 rounded-full bg-primary ring-4 ring-background" />
                <div className="at-num text-xs text-muted-foreground">
                  {formatPlainDate(event.date)}
                </div>
                <div className="text-sm">{event.title}</div>
                {event.detail && (
                  <div className="mt-0.5 text-xs text-muted-foreground">{event.detail}</div>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The asset's CURRENT known faults.
 *
 * Live, unlike the frozen copy on each handover - fix a screen and this changes, while
 * what Priya was handed in March does not. Both exist on purpose; see the column
 * comments on Asset.knownFaults and Assignment.faultsAtHandover.
 *
 * "Not recorded" is muted, never red. Most of a register is furniture that will never
 * have a condition record, and colouring every chair as a problem is precisely what
 * teaches people to stop reading the colour.
 */
function KnownCondition({ asset }: { asset: AssetDetail }) {
  if (asset.conditionCheckedAt === null) {
    return (
      <p className="mt-3 rounded-md bg-muted/40 p-2.5 text-[11px] text-muted-foreground">
        Condition has never been recorded for this asset.
      </p>
    );
  }

  const checked = formatPlainDate(asset.conditionCheckedAt.slice(0, 10));

  if (asset.knownFaults.length === 0 && !asset.conditionNotes) {
    return (
      <p className="mt-3 rounded-md bg-muted/40 p-2.5 text-[11px] text-muted-foreground">
        Checked <span className="at-num">{checked}</span> — nothing wrong.
      </p>
    );
  }

  return (
    <div className="mt-3 space-y-1.5 rounded-md border border-warning/40 bg-warning/5 p-2.5">
      <p className="text-[11px] font-medium">
        Known faults, as of <span className="at-num">{checked}</span>
      </p>
      <div className="flex flex-wrap gap-1">
        {asset.knownFaults.map((fault) => (
          <span
            key={fault}
            className="rounded-full border border-warning/40 px-2 py-0.5 text-[11px] text-warning"
          >
            {fault}
          </span>
        ))}
      </div>
      {asset.conditionNotes && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {asset.conditionNotes}
        </p>
      )}
    </div>
  );
}

/**
 * The login collected when a machine came back.
 *
 * The password is NOT in the page payload - it is fetched by its own request, which
 * writes an audit row naming who looked. So this renders a button, never a value, and
 * what comes back is held in local state and never cached.
 */
function DeviceCredential({
  assignment,
  canManage,
}: {
  assignment: AssetDetail['assignments'][number];
  canManage: boolean;
}) {
  const reveal = useRevealDeviceCredential();
  const clear = useClearDeviceCredential();
  const [shown, setShown] = React.useState<{ username: string; password: string } | null>(null);

  if (assignment.deviceUsername === null) return null;

  // Cleared: the username and the fact of the clearing both survive, so this reads as
  // a job finished rather than as a login nobody ever collected.
  if (!assignment.hasDeviceSecret) {
    return (
      <span className="w-full text-muted-foreground">
        Device login <span className="font-mono">{assignment.deviceUsername}</span> — password
        cleared
        {assignment.deviceSecretClearedAt &&
          ` ${formatPlainDate(assignment.deviceSecretClearedAt.slice(0, 10))}`}
      </span>
    );
  }

  if (!canManage) {
    return (
      <span className="w-full text-muted-foreground">
        Device login recorded for <span className="font-mono">{assignment.deviceUsername}</span>
      </span>
    );
  }

  return (
    <span className="flex w-full flex-wrap items-center gap-2">
      <span className="text-muted-foreground">
        Device login <span className="font-mono">{assignment.deviceUsername}</span>
      </span>

      {shown ? (
        <>
          <code className="rounded border bg-muted px-1.5 py-0.5 text-xs">{shown.password}</code>
          <Button hint={"Hide the password again"} size="sm" variant="ghost" className="h-6 px-2" onClick={() => setShown(null)}>
            Hide
          </Button>
        </>
      ) : (
        <Button hint={"Show the stored login for this device (recorded in the audit log)"}
          size="sm"
          variant="secondary"
          className="h-6 px-2"
          disabled={reveal.isPending}
          onClick={async () => {
            try {
              const result = await reveal.mutateAsync(assignment.id);
              setShown({ username: result.username, password: result.password });
            } catch (error) {
              toast.error(
                error instanceof ApiError ? error.message : 'That password could not be read.',
              );
            }
          }}
        >
          Show password
        </Button>
      )}

      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-destructive"
        disabled={clear.isPending}
        onClick={async () => {
          try {
            await clear.mutateAsync(assignment.id);
            setShown(null);
            toast.success('Stored password deleted.');
          } catch (error) {
            toast.error(error instanceof ApiError ? error.message : 'That could not be deleted.');
          }
        }}
      >
        Delete
      </Button>

      {shown && (
        <span className="w-full text-[11px] text-muted-foreground">
          Your name and the time are now in the audit log against this asset.
        </span>
      )}
    </span>
  );
}

/**
 * Thumbnails of one moment in an asset's life, opening into the slider.
 *
 * Its own component rather than inline JSX because it holds a hook, and the timeline
 * builds its events in a loop - calling useLightbox in there would change the number of
 * hooks per render as soon as an asset gained a handover.
 */
function PhotoRow({
  photos,
  label,
}: {
  photos: Array<{ id: string; filename: string; mimeType: string }>;
  label: string;
}) {
  const lightbox = useLightbox(
    photos
      .filter((photo) => photo.mimeType.startsWith('image/'))
      .map((photo) => ({
        id: photo.id,
        url: documentUrl(photo.id),
        filename: photo.filename,
      })),
  );

  if (photos.length === 0) return null;

  return (
    <span className="flex w-full flex-wrap items-center gap-1.5">
      <span className="text-[11px] text-muted-foreground">{label}:</span>
      {photos.map((photo) => (
        <button
          key={photo.id}
          type="button"
          title={photo.filename}
          aria-label={`Open ${photo.filename}`}
          onClick={() => lightbox.open(photo.id)}
          className="rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <img
            src={documentUrl(photo.id)}
            alt={photo.filename}
            loading="lazy"
            className="size-12 rounded border object-cover transition-opacity hover:opacity-80"
          />
        </button>
      ))}
      {lightbox.element}
    </span>
  );
}
